import {
  Category,
  DisplaySubMetric,
  EvaluationColor,
  InsightMetric,
} from '@repo/types';
import type { Color } from '@repo/ui/components/GaugeChart/getConfig.js';
import {
  getCategoryEvaluationOrder,
  type CategoryEvaluation,
} from '@repo/utils';
import { BadgeState } from 'components';
import type { KeyInsightsSectionsKit } from 'services/kits/types/keyInsightsSectionKit.types';
import type { KitSummaryScore } from 'services/kits/useKitSummaryScores';
import type { MainFinding } from 'views/Results/Summary/SummaryCards/NoteCard/useMainFindings';

export const PRO_CATEGORY_NAME = 'PRO practitioner markers';

type CountCategoryEvaluationsReturnType = Record<EvaluationColor, number>;

export const categoryAnchorId = (category: Category) =>
  `category-${category.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

export function countCategoryEvaluations(
  category: Category,
): CountCategoryEvaluationsReturnType {
  const initialState: Record<EvaluationColor, number> = {
    green: 0,
    orange: 0,
    red: 0,
    grey: 0,
  };

  return category.orderedMetrics.reduce((acc, metric) => {
    metric.orderedSubmetrics.forEach(
      submetric => acc[submetric.evaluation_color]++,
    );

    return acc;
  }, initialState);
}

// countCategoryEvaluations for surfaces with a flat submetric list instead of the
// category tree: groups on the section display_title so counts key by category name.
export function countEvaluationsByCategoryName(
  metrics: InsightMetric[],
): Map<string, CountCategoryEvaluationsReturnType> {
  const countsByCategoryName = new Map<
    string,
    CountCategoryEvaluationsReturnType
  >();

  for (const metric of metrics) {
    const categoryName =
      metric.key_insights_category?.key_insights_sections?.display_title;
    if (!categoryName) continue;

    const counts =
      countsByCategoryName.get(categoryName) ??
      { green: 0, orange: 0, red: 0, grey: 0 };
    counts[metric.evaluation_color]++;
    countsByCategoryName.set(categoryName, counts);
  }

  return countsByCategoryName;
}

export function calculateNavigationBadge(
  { red, orange }: CountCategoryEvaluationsReturnType,
  isPro: boolean,
): [string, BadgeState] {
  if (red >= 1 || orange >= 4) return ['Support', 'bad'];

  if (orange >= 1) return isPro ? ['Borderline', 'ok'] : ['Improve', 'ok'];

  return isPro ? ['Typical', 'good'] : ['Great', 'good'];
}

const PRO_GAUGE_COLOR_TO_BADGE: Record<Color, [string, BadgeState]> = {
  red: ['Support', 'bad'],
  orange: ['Borderline', 'ok'],
  grey: ['Typical', 'good'],
};

// PRO badge follows the authoritative pro_mss gauge, not the count-based badge.
// Returns null when there's no pro_mss row so the caller falls back to counts.
export function getProNavigationBadge(
  proSummaryScore: KitSummaryScore | undefined,
): [string, BadgeState] | null {
  if (!proSummaryScore?.evaluation_color) return null;

  return PRO_GAUGE_COLOR_TO_BADGE[proSummaryScore.evaluation_color];
}

const SECTION_EVALUATION_TO_BADGE: Record<CategoryEvaluation, [string, BadgeState]> = {
  Support: ['Support', 'bad'],
  Improve: ['Improve', 'ok'],
  Great: ['Great', 'good'],
};

// Returns null when there's no evaluation so the caller falls back to counts.
export function getSectionEvaluationBadge(
  evaluation?: string,
): [string, BadgeState] | null {
  if (!evaluation) return null;

  return SECTION_EVALUATION_TO_BADGE[evaluation as CategoryEvaluation] ?? null;
}

export const TOP_PRIORITIES_COUNT = 3;

export function getTopThreePrioritiesFromSubmetrics(
  submetrics: DisplaySubMetric[],
) {
  return submetrics
    .filter(submetric => submetric.priority != null)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .slice(0, TOP_PRIORITIES_COUNT);
}

export function getTopThreePrioritiesFromCategories(categories: Category[]) {
  return getTopThreePrioritiesFromSubmetrics(
    categories.flatMap(category =>
      category.orderedMetrics.flatMap(metric => metric.orderedSubmetrics),
    ),
  );
}

export function getTopPriorityRankMap(
  categories: Category[],
): Map<number, number> {
  return new Map(
    getTopThreePrioritiesFromCategories(categories).map((submetric, index) => [
      submetric.sample_health_id,
      index + 1,
    ]),
  );
}

const CATEGORY_STATUS_ORDER: Record<BadgeState, number> = {
  bad: 0, // Support
  ok: 1, // Improve
  good: 2, // Great
  new: 3,
  default: 4,
};

function categorySortKey(
  category: Category,
  sectionEvaluations?: Map<string, KeyInsightsSectionsKit>,
): [number, number] {
  // Tier from the table so a card sorts into the group its badge shows;
  // within-tier order stays lowest flagged priority.
  const sectionOrder = getCategoryEvaluationOrder(
    sectionEvaluations?.get(category.name)?.section_evaluation,
  );
  const tier = Number.isFinite(sectionOrder)
    ? sectionOrder
    : CATEGORY_STATUS_ORDER[
        calculateNavigationBadge(countCategoryEvaluations(category), false)[1]
      ];

  const priorities = category.orderedMetrics
    .flatMap(metric => metric.orderedSubmetrics)
    .filter(
      submetric =>
        ['red', 'orange'].includes(submetric.evaluation_color) &&
        submetric.priority != null,
    )
    .map(submetric => submetric.priority as number);

  const lowestPriority = priorities.length ? Math.min(...priorities) : Infinity;

  return [tier, lowestPriority];
}

export function sortCategoriesByStatusAndPriority(
  categories: Category[],
  sectionEvaluations?: Map<string, KeyInsightsSectionsKit>,
): Category[] {
  return [...(categories ?? [])].sort((a, b) => {
    const [aTier, aPriority] = categorySortKey(a, sectionEvaluations);
    const [bTier, bPriority] = categorySortKey(b, sectionEvaluations);
    return aTier - bTier || aPriority - bPriority;
  });
}

function sectionEvaluationSortKey(
  sectionEvaluation: KeyInsightsSectionsKit,
  priorityByCategoryName: Map<string, number>,
  evaluationCountsByCategoryName: Map<string, CountCategoryEvaluationsReturnType>,
): [number, number] {
  const categoryName = sectionEvaluation.section?.display_title ?? '';
  // Fall back to the count-based badge when there's no section evaluation, so a
  // card sorts into the same tier its badge shows (mirrors categorySortKey).
  const state =
    getSectionEvaluationBadge(sectionEvaluation.section_evaluation)?.[1] ??
    calculateNavigationBadge(
      evaluationCountsByCategoryName.get(categoryName) ?? {
        green: 0,
        orange: 0,
        red: 0,
        grey: 0,
      },
      false,
    )[1];
  const priority = priorityByCategoryName.get(categoryName) ?? Infinity;

  return [CATEGORY_STATUS_ORDER[state], priority];
}

// Like sortCategoriesByStatusAndPriority, but for surfaces with section evaluations +
// main findings instead of the category tree: tier from the eval, tiebreak from findings.
export function sortSectionEvaluationsByStatusAndPriority(
  sectionEvaluations: KeyInsightsSectionsKit[],
  mainFindings: MainFinding[],
  evaluationCountsByCategoryName: Map<string, CountCategoryEvaluationsReturnType>,
): KeyInsightsSectionsKit[] {
  const priorityByCategoryName = new Map<string, number>();
  for (const finding of mainFindings) {
    const categoryName = finding.gvh?.kic?.kis?.display_title;
    if (!categoryName || finding.priority == null) continue;

    const currentLowest = priorityByCategoryName.get(categoryName);
    if (currentLowest == null || finding.priority < currentLowest) {
      priorityByCategoryName.set(categoryName, finding.priority);
    }
  }

  return [...sectionEvaluations].sort((a, b) => {
    const [aTier, aPriority] = sectionEvaluationSortKey(
      a,
      priorityByCategoryName,
      evaluationCountsByCategoryName,
    );
    const [bTier, bPriority] = sectionEvaluationSortKey(
      b,
      priorityByCategoryName,
      evaluationCountsByCategoryName,
    );
    return aTier - bTier || aPriority - bPriority;
  });
}
